import React from 'react';
import { motion } from 'framer-motion';
import { cn, handleImageError } from '../../lib/utils';

// Create properly typed motion components
const MotionDiv = motion.div;

interface ClientLogoProps {
  logos: Array<{
    src: string;
    alt: string;
    width?: number;
    height?: number;
  }>;
  className?: string;
}

export function ClientLogo({ logos, className }: ClientLogoProps) {
  return (
    <div className={cn("w-full overflow-hidden bg-gradient-to-r from-gray-50/80 via-white to-gray-50/80 backdrop-blur-sm", className)}>
      <div className="relative py-12">
        {/* Logo cards */}
        <div className="relative z-10">
          {/* First row - moving right */}
          <MotionDiv
            className="flex items-center gap-12 py-6 mb-8"
            animate={{
              x: ["0%", "-25%"],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            {Array(6).fill(logos).flat().map((logo, index) => (
              <MotionDiv
                key={`logo-row1-${index}`}
                className="flex items-center justify-center min-w-[260px] h-24 px-6 bg-white/95 backdrop-blur-xl rounded-xl shadow-md border border-slate-200/60 hover:shadow-xl hover:border-primary/20 group"
                whileHover={{ 
                  scale: 1.05, 
                  filter: "grayscale(0%)",
                  y: -4,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                initial={{ filter: "grayscale(50%)" }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width || 180}
                  height={logo.height || 90}
                  className="max-h-20 max-w-full object-contain transition-all duration-300 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => handleImageError(e, undefined, () => {
                    console.log(`Failed to load client logo: ${logo.src}`);
                  })}
                />
              </MotionDiv>
            ))}
          </MotionDiv>

          {/* Second row - logos moving left */}
          <MotionDiv
            className="flex items-center gap-12 py-6"
            animate={{
              x: ["-25%", "0%"], // Opposite direction
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            {Array(6).fill(logos).flat().map((logo, index) => (
              <MotionDiv
                key={`logo-row2-${index}`}
                className="flex items-center justify-center min-w-[260px] h-24 px-6 bg-gradient-to-br from-white to-gray-50/95 backdrop-blur-xl rounded-xl shadow-md border border-slate-200/60 hover:shadow-xl hover:border-primary/20 group"
                whileHover={{ 
                  scale: 1.05, 
                  filter: "grayscale(0%)",
                  y: -4,
                  transition: { duration: 0.3 }
                }}
                initial={{ filter: "grayscale(60%)" }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width || 180}
                  height={logo.height || 90}
                  className="max-h-20 max-w-full object-contain transition-all duration-300"
                  loading="lazy"
                  onError={(e) => handleImageError(e, undefined, () => {
                    console.log(`Failed to load client logo: ${logo.src}`);
                  })}
                />
              </MotionDiv>
            ))}
          </MotionDiv>
        </div>

        {/* Enhanced gradient overlays for seamless edges */}
        <div className="absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-gray-50/80 via-gray-50/60 to-transparent pointer-events-none z-20" />
        <div className="absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-gray-50/80 via-gray-50/60 to-transparent pointer-events-none z-20" />
        
        {/* Subtle depth overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-100/10 to-transparent pointer-events-none" />
      </div>
    </div>
  );
}

export default ClientLogo;


